/**
 * README "The card". No toolbar band — the plan caps this list in single
 * digits, so search/status filters would be furniture (see README's own
 * note on where a toolbar would slot in if that ever changes).
 */
import React, { useCallback } from "react";
import Card from "../../theme/untitled/Card/Card";
import EmptyState from "../../theme/untitled/EmptyState/EmptyState";
import Icon from "../../theme/untitled/icons/Icon";
import WebsitesTable from "./Table";
import RowErrorStrip from "./RowErrorStrip";
import styles from "./WebsitesCard.module.scss";

const QuotaClause = ({ domainsUsed, domainLimit, domainsFree, onUpgrade }) => {
  if (domainLimit == null || domainLimit < 0) {
    return <span className={styles.footerQuota}>Unlimited domains</span>;
  }

  return (
    <span className={styles.footerQuota}>
      {domainsUsed} of {domainLimit} domains used
      <i>·</i>
      {domainsFree === 0 ? (
        <span className={styles.quotaWarn}>
          <Icon name="AlertCircle" size={16} />
          No domains left — remove one or upgrade to add more
        </span>
      ) : (
        <span>
          {domainsFree} left
        </span>
      )}
      <i>·</i>
      <button type="button" className={styles.upgradeLink} onClick={onUpgrade}>
        Upgrade plan
      </button>
    </span>
  );
};

const WebsitesCard = ({
  domains,
  loading,
  error,
  onRetryFetch,
  activeCount,
  pausedCount,
  domainsUsed,
  domainLimit,
  domainsFree,
  onUpgrade,
  busyRowId,
  rowError,
  onDismissRowError,
  onRetryRowAction,
  renderMenu,
  onOpenAdd,
}) => {
  const countsText =
    activeCount + pausedCount === 0
      ? "No websites yet"
      : `${activeCount} active · ${pausedCount} paused`;

  // onRetryRowAction ignores the domain it's given (DomainsPage.js's
  // version already closes over the right retry logic via rowError.retry),
  // so it can be passed straight through here without a wrapping closure.
  const rowExtra = useCallback(
    (domain) =>
      rowError?.rowId === domain.uuid ? (
        <RowErrorStrip
          domain={domain}
          message={rowError.message}
          onRetry={onRetryRowAction}
          onDismiss={onDismissRowError}
          colSpan={4}
        />
      ) : null,
    [rowError, onRetryRowAction, onDismissRowError]
  );

  let body;
  if (loading) {
    body = (
      <WebsitesTable
        rows={[]}
        emptyState={<p className={styles.loadingText}>Loading websites&hellip;</p>}
      />
    );
  } else if (error) {
    body = (
      <WebsitesTable
        rows={[]}
        emptyState={
          <EmptyState
            variant="error"
            icon={<Icon name="AlertCircle" size={24} />}
            title="Couldn't load websites"
            body="The service didn't respond. Try again in a moment."
            actions={[{ label: "Retry", onClick: onRetryFetch }]}
          />
        }
      />
    );
  } else if (domains.length === 0) {
    body = (
      <WebsitesTable
        rows={[]}
        emptyState={
          <div className={styles.emptyWrap}>
            <EmptyState
              icon={<Icon name="GlobeSlated01" size={24} />}
              title="No websites yet"
              body="Add the domain of the site where you will embed your widgets. Until then, every embed is refused."
              actions={[{ label: "Add website", hierarchy: "primary", onClick: onOpenAdd }]}
            />
            <p className={styles.emptyAside}>
              Sharing a widget&rsquo;s own URL works without adding a website
            </p>
          </div>
        }
      />
    );
  } else {
    body = (
      <WebsitesTable
        rows={domains}
        renderMenu={renderMenu}
        busyRowId={busyRowId}
        rowExtra={rowExtra}
      />
    );
  }

  return (
    <Card aria-labelledby="websites-title">
      <Card.Header
        id="websites-title"
        title="Allowed domains"
        subtitle="Widgets load only on these domains. On any other domain the browser refuses the embed."
      />

      <div className={styles.tableScroll}>{body}</div>

      <div className={styles.footer}>
        <QuotaClause
          domainsUsed={domainsUsed}
          domainLimit={domainLimit}
          domainsFree={domainsFree}
          onUpgrade={onUpgrade}
        />
        <span className={styles.footerRight}>{countsText}</span>
      </div>
    </Card>
  );
};

export default WebsitesCard;
